import { dom } from "@fortawesome/fontawesome-svg-core";
import { layers, namedFlavor } from "@protomaps/basemaps";
import * as Sentry from "@sentry/browser";
import debounce from "lodash/debounce";
import * as maplibregl from "maplibre-gl";
import {
  type LayerSpecification,
  LngLat,
  type LngLatBounds,
  type MapMovementEvent,
  type Marker,
} from "maplibre-gl";
import { Protocol } from "pmtiles";
import { cleanupFavoriteRive, initializeFavoriteRive } from "../../components/rive/favorite-btn";
import { mapLocale } from "../../utils/map-locale";
import { supportsWebGl2 } from "../../utils/webgl";

// v6 is ESM-only; import.meta.url doesn't reliably resolve to the worker
// file inside rspack's module graph, so the worker URL needs setting
// explicitly — see "setWorkerUrl() is bundler-only":
// https://maplibre.org/maplibre-gl-js/docs/guides/v5-to-v6-migration-guide/#setworkerurl-is-bundler-only
// The worker and its shared.mjs sibling are copied to a version-namespaced
// directory (rsbuild.config.ts) rather than through rspack's default
// content-hashed asset pipeline — see that config for why.
maplibregl.setWorkerUrl(
  `/inc/assets/maplibre-gl-${process.env.MAPLIBRE_GL_VERSION}/maplibre-gl-worker.mjs`,
);

export const initSearch = () => {
  const search = document.getElementById("search") as HTMLDivElement;

  if (!search) {
    return;
  }

  const body = document.querySelector("body") as HTMLBodyElement;

  /// Setup
  setupRtlPlugin();
  setupFilters(search);
  setupToggleVideo(search);
  setupMapButtonsEvents(search, body);
  setupShowLanguagesButton(search);
  ///

  const mapMarkerWidth = `${40}px`;
  const mapMarkerHeight = `${40}px`;
  // Active icons 10% larger
  const mapMarkerActiveWidth = `${44}px`;
  const mapMarkerActiveHeight = `${44}px`;

  const paginationPages: NodeListOf<HTMLLinkElement> = search.querySelectorAll(".pagination a");

  paginationPages.forEach((paginationPage) => {
    managePaginationPage(paginationPage);
  });

  function managePaginationPage(paginationPage: HTMLLinkElement) {
    paginationPage.addEventListener("click", (e) => {
      e.preventDefault();

      const deeplinkUrl = paginationPage.getAttribute("href") as string;
      searchAction(deeplinkUrl, false);
    });
  }

  const profileCards: NodeListOf<HTMLDivElement> = search.querySelectorAll(".profile-card");

  profileCards.forEach((profileCard) => {
    manageProfileCards(profileCard);
  });

  function manageProfileCards(profileCard: HTMLDivElement) {
    profileCard.addEventListener("mouseenter", () => {
      const geohash = profileCard.dataset.geohash;

      markersList.forEach((marker) => {
        // First set all markers to the default markerIcon
        // @ts-expect-error: markerIcon prop
        marker._element.style.backgroundImage = `url(${marker._element.markerIcon})`;
        marker._element.style.width = mapMarkerWidth;
        marker._element.style.height = mapMarkerHeight;
        marker._element.style.zIndex = "0";

        // set this profile geohash marker to active
        // @ts-expect-error: geohash prop
        if (marker._element.geohash === geohash) {
          // @ts-expect-error: markerIconActive prop
          marker._element.style.backgroundImage = `url(${marker._element.markerIconActive})`;
          marker._element.style.width = mapMarkerActiveWidth;
          marker._element.style.height = mapMarkerActiveHeight;
          marker._element.style.zIndex = "1";
        }
      });
    });

    profileCard.addEventListener(
      "touchstart",
      () => {
        const geohash = profileCard.dataset.geohash;

        markersList.forEach((marker) => {
          // First set all markers to the default markerIcon
          // @ts-expect-error: markerIcon prop
          marker._element.style.backgroundImage = `url(${marker._element.markerIcon})`;
          marker._element.style.width = mapMarkerWidth;
          marker._element.style.height = mapMarkerHeight;
          marker._element.style.zIndex = "0";

          // set this profile geohash marker to active
          // @ts-expect-error: geohash prop
          if (marker._element.geohash === geohash) {
            // @ts-expect-error: markerIconActive prop
            marker._element.style.backgroundImage = `url(${marker._element.markerIconActive})`;
            marker._element.style.width = mapMarkerActiveWidth;
            marker._element.style.height = mapMarkerActiveHeight;
            marker._element.style.zIndex = "1";
          }
        });
      },
      { passive: true },
    );

    profileCard.addEventListener("mouseleave", () => {
      const geohash = profileCard.dataset.geohash;

      markersList.forEach((marker) => {
        // @ts-expect-error: geohash prop
        if (marker._element.geohash === geohash) {
          // @ts-expect-error: markerIcon prop
          marker._element.style.backgroundImage = `url(${marker._element.markerIcon})`;
          marker._element.style.width = mapMarkerWidth;
          marker._element.style.height = mapMarkerHeight;
          marker._element.style.zIndex = "0";
        }
      });
    });
  }

  const buttonMapShow = search.querySelector("button.map-show") as HTMLButtonElement;

  if (buttonMapShow) {
    buttonMapShow.addEventListener("click", () => {
      body.classList.add("mapvisible");
      initialize_maps(); // Load maps
    });
  }

  function searchAction(deeplinkUrl: string, updateMarkers = true, updatePushState = true) {
    /* Default parameter values, IE11 and Edge tested */
    updateMarkers = typeof updateMarkers !== "undefined" ? updateMarkers : true;
    updatePushState = typeof updatePushState !== "undefined" ? updatePushState : true;

    const viewResultsButton = search.querySelector("button.view-results") as HTMLButtonElement;

    viewResultsButton.innerHTML =
      '<span role="status" class="spinner-border spinner-border-sm"><span class="visually-hidden">...</span></span>';

    if (deeplinkUrl.includes("geohash")) {
      body.classList.add("geohashsearch");
    } else {
      body.classList.remove("geohashsearch");
    }

    const loading = search.querySelector(".profile-cards .loading") as HTMLDivElement;
    const loadingSpinner = search.querySelector(
      ".profile-cards .loading-spinner",
    ) as HTMLDivElement;

    /* Grey out result list */
    loading.style.display = "block";

    /* Scroll to top of the document */
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }, 400);

    /* After 2000 milsec add loadingSpinner */
    setTimeout(() => {
      loadingSpinner.style.display = "block";
    }, 2000);

    if (updatePushState) {
      history.pushState({}, "", deeplinkUrl);
    }

    debounceGetSearchListResults(deeplinkUrl);

    if (window.mapSearch && updateMarkers) {
      setMarkers(); // load new markers
    }
  }

  const debounceGetSearchListResults = debounce(getSearchListResults, 500);

  function getSearchListResults(deeplinkUrl: string) {
    const viewResultsButton = search.querySelector("button.view-results") as HTMLButtonElement;
    const loading = search.querySelector(".profile-cards .loading") as HTMLDivElement;

    const loadingSpinner = search.querySelector(
      ".profile-cards .loading-spinner",
    ) as HTMLDivElement;

    fetch(deeplinkUrl, { credentials: "same-origin", method: "get" })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Search request failed: ${response.status} ${response.statusText}`);
        }

        return response.text().then((responseHtmlText) => ({ response, responseHtmlText }));
      })
      .then(({ response, responseHtmlText }) => {
        const parser = new DOMParser();
        const responseHtml = parser.parseFromString(responseHtmlText, "text/html");

        if (!responseHtml.querySelector(".results")) {
          // Not a results page. The backend answered the search URL with a redirect to another
          // page, e.g. the login page once the session has ended; fetch follows the 302
          // transparently, so the only trace left is the final URL. Finish that navigation
          // instead of grafting the other page into the results list.
          if (response.redirected) {
            window.location.assign(response.url);

            return;
          }

          throw new Error(`Search response is not a results page: ${response.url}`);
        }

        const pageH1 = search.querySelector(".results h1") as HTMLHeadingElement;
        const responseH1 = responseHtml.querySelector(".results h1") as HTMLHeadingElement;
        pageH1.textContent = responseH1.textContent;

        const pageLocationDescription = search.querySelector(
          ".results .location-description",
        ) as HTMLDivElement;
        const responseLocation = responseHtml.querySelector(
          ".results .location-description",
        ) as HTMLDivElement;
        pageLocationDescription.textContent = responseLocation.textContent;

        const pageLocationPayoff = search.querySelector(
          ".results .location-payoff",
        ) as HTMLDivElement;

        if (pageLocationPayoff) {
          pageLocationPayoff.textContent = ""; // do not show payoff when continue searching
        }

        const noResultsCta = search.querySelector(".results .no-results-cta") as HTMLDivElement;
        const responseNoResultsCta = responseHtml.querySelector(
          ".results .no-results-cta",
        ) as HTMLDivElement;
        noResultsCta.innerHTML = responseNoResultsCta.innerHTML;

        const savedSearchButtonOneClick = document.getElementById(
          "saved-search-button-one-click",
        ) as HTMLButtonElement;

        if (savedSearchButtonOneClick) {
          manageSavedSearchButtonOneClick(savedSearchButtonOneClick);
        }

        const resultList = search.querySelector(".results .result-list") as HTMLDivElement;
        const responseResultList = responseHtml.querySelector(
          ".results .result-list",
        ) as HTMLDivElement;
        resultList.innerHTML = responseResultList.innerHTML;

        /* Start Rive favorites buttons block */
        cleanupFavoriteRive();
        initializeFavoriteRive();
        /* End Rive favorites buttons block */

        const trimmedMean = search.querySelector(".trimmed-mean") as HTMLDivElement;

        if (trimmedMean) {
          const responseTrimmedMean = responseHtml.querySelector(".trimmed-mean") as HTMLDivElement;
          trimmedMean.innerHTML = responseTrimmedMean.innerHTML;
        }

        /* Start Filters count */
        const activeFiltersCount = search.querySelector(".active-filters-count") as HTMLSpanElement;

        if (activeFiltersCount) {
          const activeFiltersCountNew = responseHtml.querySelector(
            ".active-filters-count",
          ) as HTMLSpanElement;
          activeFiltersCount.textContent = activeFiltersCountNew.textContent;

          const countValue = activeFiltersCountNew.dataset.count as string;

          if (parseInt(countValue, 10) === 0) {
            activeFiltersCount.classList.add("d-none");
          } else {
            activeFiltersCount.classList.remove("d-none");
          }
        }
        /* End Filters count */

        /* Start More Filters count */
        const activeMoreFiltersCount = search.querySelector(
          ".active-more-filters-count",
        ) as HTMLSpanElement;

        if (activeMoreFiltersCount) {
          const activeMoreFiltersCountNew = responseHtml.querySelector(
            ".active-more-filters-count",
          ) as HTMLSpanElement;

          activeMoreFiltersCount.textContent = activeMoreFiltersCountNew.textContent;

          const moreFilterValue = activeMoreFiltersCountNew.dataset.count as string;

          if (parseInt(moreFilterValue, 10) === 0) {
            activeMoreFiltersCount.classList.add("d-none");
          } else {
            activeMoreFiltersCount.classList.remove("d-none");
          }
        }
        /* End More Filters count */

        /* Start Verification Filters count */
        const activeVerificationFiltersCount = search.querySelector(
          ".active-verification-filters-count",
        ) as HTMLSpanElement;

        if (activeVerificationFiltersCount) {
          const activeVerificationFiltersCountNew = responseHtml.querySelector(
            ".active-verification-filters-count",
          ) as HTMLSpanElement;
          activeVerificationFiltersCount.textContent =
            activeVerificationFiltersCountNew.textContent;

          const activeVerificationFiltersCountValue = activeVerificationFiltersCountNew.dataset
            .count as string;

          if (parseInt(activeVerificationFiltersCountValue, 10) === 0) {
            activeVerificationFiltersCount.classList.add("d-none");
          } else {
            activeVerificationFiltersCount.classList.remove("d-none");
          }
        }
        /* End Verification Filters count */

        /* Start Children Filters count */
        const activeChildrenFiltersCount = search.querySelector(
          ".active-children-filters-count",
        ) as HTMLSpanElement;

        if (activeChildrenFiltersCount) {
          const activeChildrenFiltersCountNew = responseHtml.querySelector(
            ".active-children-filters-count",
          ) as HTMLSpanElement;
          activeChildrenFiltersCount.textContent = activeChildrenFiltersCountNew.textContent;

          const activeChildrenFiltersCountNewValue = activeChildrenFiltersCountNew.dataset
            .count as string;

          if (parseInt(activeChildrenFiltersCountNewValue, 10) === 0) {
            activeChildrenFiltersCount.classList.add("d-none");
          } else {
            activeChildrenFiltersCount.classList.remove("d-none");
          }
        }
        /* End Children Filters count */

        /* Start UserType Filters count */
        const activeUserTypeFiltersCount = search.querySelector(
          ".active-user-type-filters-count",
        ) as HTMLSpanElement;

        if (activeUserTypeFiltersCount) {
          const activeUserTypeFiltersCountNew = responseHtml.querySelector(
            ".active-user-type-filters-count",
          ) as HTMLSpanElement;

          activeUserTypeFiltersCount.textContent = activeUserTypeFiltersCountNew.textContent;

          if (parseInt(activeUserTypeFiltersCountNew.dataset.count as string, 10) === 0) {
            activeUserTypeFiltersCount.classList.add("d-none");
          } else {
            activeUserTypeFiltersCount.classList.remove("d-none");
          }
        }
        /* End UserType Filters count */

        /* Start TypeBabysit Filters count */
        const activeTypeBabysitFiltersCount = search.querySelector(
          ".active-type-babysit-filters-count",
        );
        if (activeTypeBabysitFiltersCount) {
          const activeTypeBabysitFiltersCountNew = responseHtml.querySelector(
            ".active-type-babysit-filters-count",
          ) as HTMLSpanElement;

          activeTypeBabysitFiltersCount.textContent = activeTypeBabysitFiltersCountNew.textContent;

          if (parseInt(activeTypeBabysitFiltersCountNew.dataset.count as string, 10) === 0) {
            activeTypeBabysitFiltersCount.classList.add("d-none");
          } else {
            activeTypeBabysitFiltersCount.classList.remove("d-none");
          }
        }
        /* End TypeBabysit Filters count */

        /* Start Experience Filters count */
        const activeExperienceFiltersCount = search.querySelector(
          ".active-experience-filters-count",
        ) as HTMLSpanElement;

        if (activeExperienceFiltersCount) {
          const activeExperienceFiltersCountNew = responseHtml.querySelector(
            ".active-experience-filters-count",
          ) as HTMLSpanElement;

          activeExperienceFiltersCount.textContent = activeExperienceFiltersCountNew.textContent;

          if (parseInt(activeExperienceFiltersCountNew.dataset.count as string, 10) === 0) {
            activeExperienceFiltersCount.classList.add("d-none");
          } else {
            activeExperienceFiltersCount.classList.remove("d-none");
          }
        }
        /* End Experience Filters count */

        const savedSearchParams = document.getElementById(
          "saved-search-params",
        ) as HTMLInputElement;

        if (savedSearchParams) {
          const responseSavedSearchParams = responseHtml.getElementById(
            "saved-search-params",
          ) as HTMLInputElement;

          // The partial only renders this element for a logged-in user (see
          // saved_search_form.html.twig); the session can expire between the
          // initial page load and this AJAX request, so it may be absent here
          // even though it's still present in the current page's DOM.
          if (responseSavedSearchParams) {
            savedSearchParams.value = responseSavedSearchParams.value;
          }
        }

        const toggleVideos: NodeListOf<HTMLButtonElement> =
          search.querySelectorAll(".toggle-video");

        toggleVideos.forEach((toggleVideo) => {
          manageToggleVideo(toggleVideo);
        });

        const paginationPages: NodeListOf<HTMLLinkElement> =
          search.querySelectorAll(".pagination a");

        paginationPages.forEach((paginationPage) => {
          managePaginationPage(paginationPage);
        });

        const profileCards: NodeListOf<HTMLDivElement> = search.querySelectorAll(".profile-card");

        profileCards.forEach((profileCard) => {
          manageProfileCards(profileCard);
        });

        const reponseViewResultsButton = responseHtml.querySelector(
          "button.view-results",
        ) as HTMLButtonElement;

        viewResultsButton.textContent = reponseViewResultsButton.textContent;

        // Tell FontAwesome to search for new icons.
        // Fix the bug with reviews icons not being rendered.
        dom.i2svg();
      })
      .catch((error) => {
        Sentry.captureException(error);
        loading.style.display = "none";
        loadingSpinner.style.display = "none";
      });
  }

  const savedSearchButtonOneClick = document.getElementById(
    "saved-search-button-one-click",
  ) as HTMLButtonElement;

  if (savedSearchButtonOneClick) {
    manageSavedSearchButtonOneClick(savedSearchButtonOneClick);
  }

  /* If users pushes the browser backbutton (popstate trigger), do a new search with pushstate=false */
  window.addEventListener("popstate", (event) => {
    // Ignore popstate events caused by anchor navigation (hash changes)
    if (event.state === null && window.location.hash) {
      return;
    }

    const deeplinkUrl = window.location.href;
    searchAction(deeplinkUrl, true, false);
  });

  const filterDistances: NodeListOf<HTMLButtonElement> = document.querySelectorAll(
    "#searchfilter .distance .distance-option",
  );
  filterDistances.forEach((filterDistance) => {
    filterDistance.addEventListener("click", () => {
      const selectedDistanceValue = document.querySelector(
        "#searchfilter .distance .value",
      ) as HTMLSpanElement;
      selectedDistanceValue.textContent = filterDistance.innerText;

      const queryBuilder = new URLSearchParams(
        window.location.search.replace(/(%5B)[[\d]+(%5D)/g, "%5B%5D"),
      );

      const filterType = filterDistance.dataset.filterType?.trim();
      const filterValue = filterDistance.dataset.filterValue?.trim();

      if (filterType && filterValue) {
        queryBuilder.set(filterType, filterValue);
      }

      queryBuilder.delete("page");

      const pathname = window.location.pathname;
      const deeplinkUrl = `${pathname}?${queryBuilder.toString()}`; // Load result page

      return searchAction(deeplinkUrl);
    });
  });

  const sortOrders: NodeListOf<HTMLSpanElement> = document.querySelectorAll(
    "#searchfilter .sort-order-filter .sort-option",
  );

  sortOrders.forEach((sortOrder) => {
    sortOrder.addEventListener("click", () => {
      const selectedSortValue = document.querySelector(
        "#searchfilter .sort-order-filter .value",
      ) as HTMLSpanElement;
      selectedSortValue.textContent = sortOrder.innerText;

      const queryBuilder = new URLSearchParams(
        window.location.search.replace(/(%5B)[[\d]+(%5D)/g, "%5B%5D"),
      );

      const filterType = sortOrder.dataset.filterType?.trim();
      const filterValue = sortOrder.dataset.filterValue?.trim();

      if (filterType && filterValue) {
        queryBuilder.set(filterType, filterValue);
      }

      queryBuilder.delete("page");

      const pathname = window.location.pathname;
      const deeplinkUrl = `${pathname}?${queryBuilder.toString()}`; // Load result page

      return searchAction(deeplinkUrl);
    });
  });

  const filterCheckboxes: NodeListOf<HTMLInputElement> = document.querySelectorAll(
    "#search .checkbox-container input",
  );

  filterCheckboxes.forEach((filterCheckbox) => {
    filterCheckbox.addEventListener("change", (event) => {
      const eventTarget = event.target as HTMLInputElement;

      const queryBuilder = new URLSearchParams(
        window.location.search.replace(/(%5B)[[\d]+(%5D)/g, "%5B%5D"),
      );

      const checkboxContainer = filterCheckbox.closest(".checkbox-container") as HTMLLabelElement;

      const rawFilterType = checkboxContainer.dataset.filterType as string;
      const filterType = rawFilterType.trim();

      const rawFilterValue = checkboxContainer.dataset.filterValue as string;
      const filterValue = rawFilterValue.trim();

      /* If filterType user_type, then set default values */
      if (filterType === "user_type[]" && !queryBuilder.has("user_type[]")) {
        queryBuilder.append("user_type[]", "babysitter");
        queryBuilder.append("user_type[]", "nanny");
        queryBuilder.append("user_type[]", "childminder");
      }

      /* If filterType type_babysit, then set user_type to parent */
      if (filterType === "type_babysit[]") {
        if (eventTarget.checked) {
          queryBuilder.delete("user_type[]");
          queryBuilder.append("user_type[]", "parent");
        } else {
          queryBuilder.delete("user_type[]");
        }
      }

      /* If filterType special_needs unchecked, then also clear special_needs_types */
      if (filterType === "special_needs") {
        if (!eventTarget.checked) {
          queryBuilder.delete("special_needs_types[]");
        } else {
          const specialNeedsTypesInput: NodeListOf<HTMLInputElement> = search.querySelectorAll(
            ".special-needs-type input",
          );
          specialNeedsTypesInput.forEach((specialNeedsTypeInput) => {
            const filterLabel = specialNeedsTypeInput.closest(
              ".checkbox-container",
            ) as HTMLLabelElement;

            const rawFilterValue = filterLabel.dataset.filterValue as string;
            const filterValue = rawFilterValue.trim();

            if (specialNeedsTypeInput.checked) {
              queryBuilder.append("special_needs_types[]", filterValue);
            }
          });
        }
      }

      // If filterValue is true, then this filter type is a boolean
      if (filterValue === "true" || filterValue === "0") {
        queryBuilder.delete(filterType);
        if (eventTarget.checked) {
          queryBuilder.set(filterType, filterValue);
        }

        queryBuilder.delete("page");

        const pathname = window.location.pathname;
        const deeplinkUrl = `${pathname}?${queryBuilder.toString()}`; // Load result page

        return searchAction(deeplinkUrl);
      }

      let currentFilterValues = queryBuilder.getAll(filterType);

      currentFilterValues = currentFilterValues.filter((item) => item !== filterValue); // removes filterValue from array

      if (eventTarget.checked) {
        currentFilterValues.push(filterValue);
      }

      queryBuilder.delete(filterType);

      if (currentFilterValues) {
        currentFilterValues.forEach((value) => {
          queryBuilder.append(filterType, value);
        });
      }

      queryBuilder.delete("page");

      const pathname = window.location.pathname;
      const deeplinkUrl = `${pathname}?${queryBuilder.toString()}`; // Load result page

      return searchAction(deeplinkUrl);
    });
  });

  const map = document.getElementById("map") as HTMLDivElement;
  const mapLoader = document.getElementById("map-loader") as HTMLLabelElement;

  const setMarkerForUserLocation = (mapSearch: maplibregl.Map) => {
    /* Set user home address on the map */
    const lat = map?.dataset.forUserLat?.trim();
    const lng = map?.dataset.forUserLng?.trim();

    if (!lng || !lat) {
      return;
    }

    // create a DOM element for the marker
    const markerLocation = document.createElement("div");

    markerLocation.className = "marker-for-user-location";
    markerLocation.style.backgroundImage =
      "url(https://cdn.babysits.com/global/icons/maps/home.svg)";
    markerLocation.style.backgroundSize = "cover";
    markerLocation.style.width = `${36}px`;
    markerLocation.style.height = `${36}px`;
    markerLocation.style.zIndex = "0";
    markerLocation.style.cursor = "pointer";

    const marker = new maplibregl.Marker({ element: markerLocation })
      .setLngLat([parseFloat(lng), parseFloat(lat)])
      .addTo(mapSearch);

    const homeTooltip = map.dataset.homeTooltip?.trim();

    if (homeTooltip) {
      marker.setPopup(
        new maplibregl.Popup({ offset: 24, closeButton: false }).setText(homeTooltip),
      );
    }
  };

  const initialize_search_map = () => {
    const loadingSpinner = map.querySelector(".loading-spinner") as HTMLDivElement;

    loadingSpinner.style.display = "block";

    const mapDataset = map.dataset as { lat: string; lng: string; zoom: string };

    const lat = parseFloat(mapDataset.lat.trim());
    const lng = parseFloat(mapDataset.lng.trim());
    const zoom = parseInt(mapDataset.zoom.trim(), 10);

    const queryBuilder = new URLSearchParams(
      window.location.search.replace(/(%5B)[[\d]+(%5D)/g, "%5B%5D"),
    );

    let lngLatBounds: LngLatBounds | null = null;
    let center: LngLat = new LngLat(lng, lat);

    if (
      queryBuilder.has("ne_lat") &&
      queryBuilder.has("ne_lng") &&
      queryBuilder.has("sw_lat") &&
      queryBuilder.has("sw_lng")
    ) {
      const sw_lng = parseFloat(queryBuilder.get("sw_lng") as string);
      const sw_lat = parseFloat(queryBuilder.get("sw_lat") as string);
      const ne_lng = parseFloat(queryBuilder.get("ne_lng") as string);
      const ne_lat = parseFloat(queryBuilder.get("ne_lat") as string);

      const sw = new maplibregl.LngLat(sw_lng, sw_lat);
      let ne = new maplibregl.LngLat(ne_lng, ne_lat);

      // Assuming that coordinates cross the 180th meridian
      // Source https://github.com/mapbox/mapbox-gl-js/issues/3250
      // This is working as designed - if you need the line to cross the antimeridian, use a closer longitude beyond 180 - e.g. 179 to 188 instead of -172.
      if (ne_lng < 0 && sw_lng > 0) {
        ne = new maplibregl.LngLat(360 + ne_lng, ne_lat);
      }

      lngLatBounds = new maplibregl.LngLatBounds(sw, ne);

      center = lngLatBounds.getCenter();
    }

    const mediaQueryObj = window.matchMedia("(prefers-color-scheme: dark)");
    const isDarkMode = mediaQueryObj.matches;
    const activeMode = isDarkMode ? "dark" : "light";

    // Initialize PMTiles protocol handler before creating the map
    const protocol = new Protocol();
    maplibregl.addProtocol("pmtiles", protocol.tile);

    // Get language from html element
    const htmlLang = document.documentElement.lang; // "nl-NL"

    // Extract just the language code (before the hyphen)
    const langCode = htmlLang.split("-")[0]; // "nl"

    const mapSearch = new maplibregl.Map({
      container: "map-search",
      center: center,
      zoom: zoom,
      scrollZoom: true,
      minZoom: 5,
      maxZoom: 15,
      dragRotate: false,
      validateStyle: false, // Disable style validation for faster map load
      locale: mapLocale(map),
      style: {
        version: 8,
        glyphs: "https://maps.babysits.com/fonts/{fontstack}/{range}.pbf",
        sprite: `https://maps.babysits.com/sprites/v4/${activeMode}`,
        sources: {
          babysits_maps: {
            type: "vector",
            url: "pmtiles://https://maps.babysits.com/20251101.pmtiles",
            attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
          },
        },
        layers: layers("babysits_maps", namedFlavor(activeMode), {
          lang: langCode,
        }) as LayerSpecification[],
      },
    });

    mapSearch.touchZoomRotate.disableRotation();

    if (lngLatBounds) {
      mapSearch.fitBounds(
        lngLatBounds,
        {
          padding: { top: 0, bottom: 0, left: 0, right: 0 },
          linear: true,
          maxZoom: 15,
        },
        { initialFitBounds: true },
      );
    }

    mapSearch.addControl(
      new maplibregl.NavigationControl({
        showCompass: false,
      }),
    );

    mapSearch.addControl(
      new maplibregl.GeolocateControl({
        fitBoundsOptions: {
          maxZoom: 13,
        },
      }),
    );

    mapSearch.on("load", () => {
      setMarkers();

      if (queryBuilder.get("lat") && queryBuilder.get("lng")) {
        const rawLat = parseFloat(queryBuilder.get("lat") as string);
        const rawLng = parseFloat(queryBuilder.get("lng") as string);

        setMarkerForSearchLocation(mapSearch, rawLat, rawLng);
      }

      setMarkerForUserLocation(mapSearch);

      loadingSpinner.style.display = "none";
    });

    const unsetGeohashQuery = () => {
      const queryBuilder = new URLSearchParams(
        window.location.search.replace(/(%5B)[[\d]+(%5D)/g, "%5B%5D"),
      );

      if (!queryBuilder.has("geohash")) {
        // No geohash in request query, void
        return;
      }

      queryBuilder.delete("geohash");

      const pathname = window.location.pathname;
      const deeplinkUrl = `${pathname}?${queryBuilder.toString()}`;

      searchAction(deeplinkUrl);
    };

    /* Onclick map: remove geohash request query */
    mapSearch.on("click", () => {
      unsetGeohashQuery();
    });

    window.mapSearch = mapSearch;
  };

  let markersList: Marker[] = [];

  // Removes the markers from the map
  const clearMarkers = () => {
    if (markersList) {
      markersList.forEach((marker) => {
        marker.remove();
      });
      markersList = [];
    }
  };

  const setMarkers = () => {
    const mapSearch = window.mapSearch;

    // Set url
    const userTypeDataset = map.dataset.userTypes as string;
    const typesBabysitDataset = map.dataset.typesBabysit as string;

    const userTypes = userTypeDataset.trim().split(",");
    const typesBabysit = typesBabysitDataset.trim().split(",");

    const queryBuilderMarkers = new URLSearchParams(
      window.location.search.replace(/(%5B)[[\d]+(%5D)/g, "%5B%5D"),
    );

    queryBuilderMarkers.delete("lat");
    queryBuilderMarkers.delete("lng");
    queryBuilderMarkers.delete("geohash");
    queryBuilderMarkers.delete("page");
    queryBuilderMarkers.delete("city_id");
    queryBuilderMarkers.delete("distance");
    queryBuilderMarkers.delete("location_search");

    // "distance" sort requires lat/lng, which markers never send (bounding box is used instead)
    if (queryBuilderMarkers.get("sort_order") === "distance") {
      queryBuilderMarkers.delete("sort_order");
    }

    if (!queryBuilderMarkers.has("user_type[]") && userTypes) {
      queryBuilderMarkers.delete("user_type[]");

      userTypes.forEach((userType) => {
        if (userType) {
          queryBuilderMarkers.append("user_type[]", userType);
        }
      });
    }

    if (!queryBuilderMarkers.has("type_babysit[]") && typesBabysit) {
      queryBuilderMarkers.delete("type_babysit[]");

      typesBabysit.forEach((typeBabysit) => {
        if (typeBabysit) {
          queryBuilderMarkers.append("type_babysit[]", typeBabysit);
        }
      });
    }

    const ne = mapSearch.getBounds().getNorthEast().wrap(); // Returns a new LngLat object whose longitude is wrapped to the range (-180, 180).
    const sw = mapSearch.getBounds().getSouthWest().wrap(); // Returns a new LngLat object whose longitude is wrapped to the range (-180, 180).

    queryBuilderMarkers.set("ne_lat", ne.lat.toString());
    queryBuilderMarkers.set("ne_lng", ne.lng.toString());
    queryBuilderMarkers.set("sw_lat", sw.lat.toString());
    queryBuilderMarkers.set("sw_lng", sw.lng.toString());

    const markersApiUrl = `/website-api/user/search/markers/?${queryBuilderMarkers.toString()}`;

    mapLoader.classList.remove("d-none");

    debounceGetMarkers(markersApiUrl);

    return markersList;
  };

  const debounceGetMarkers = debounce(getMarkers, 500);

  function getMarkers(markersApiUrl: string) {
    const mapSearch = window.mapSearch;

    fetch(markersApiUrl, { credentials: "same-origin", method: "get" })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Markers request failed: ${response.status} ${response.statusText}`);
        }

        return response.json();
      })
      .then((jsonResponse) => {
        const queryBuilderGeohash = new URLSearchParams(
          window.location.search.replace(/(%5B)[[\d]+(%5D)/g, "%5B%5D"),
        );

        queryBuilderGeohash.delete("page");
        queryBuilderGeohash.delete("distance");

        /* First clear previous markers */
        clearMarkers();

        jsonResponse.data.result.forEach(
          (resultMarker: { geohash: string; amount: number; lng: number; lat: number }) => {
            const geohash = resultMarker.geohash;
            const amount = resultMarker.amount;

            const lng = resultMarker.lng;
            const lat = resultMarker.lat;

            let markerIcon = {};
            let markerIconInactive = {};
            let markerIconActive = {};

            let active = false;

            if (queryBuilderGeohash.get("geohash") === geohash) {
              active = true;
            }

            if (amount === 1) {
              markerIconInactive = "https://cdn.babysits.com/global/icons/maps/marker-1.svg";
              markerIconActive = "https://cdn.babysits.com/global/icons/maps/marker-active-1.svg";
            } else {
              let amount_marker: number | string = amount;
              // If the amount is bigger than 9, display the "9plus marker"
              if (amount > 9) {
                amount_marker = "9plus";
              }

              markerIconInactive = `https://cdn.babysits.com/global/icons/maps/marker-${amount_marker}.svg`;
              markerIconActive =
                "https://cdn.babysits.com/global/icons/maps/marker-active-" +
                amount_marker +
                ".svg";
            }

            markerIcon = markerIconInactive;

            // create a DOM element for the marker
            const el = document.createElement("div");
            // @ts-expect-error: custom activeMarker prop
            el.activeMarker = false;

            if (active) {
              markerIcon = markerIconActive;
              // @ts-expect-error: custom activeMarker prop
              el.activeMarker = true;
            }

            el.className = "marker";
            el.style.backgroundImage = `url(${markerIcon})`;
            el.style.backgroundSize = "cover";
            el.style.width = mapMarkerWidth;
            el.style.height = mapMarkerHeight;
            el.style.cursor = "pointer";
            el.style.zIndex = "0";

            // @ts-expect-error: custom geohash prop
            el.geohash = geohash; // store for later re-use
            // @ts-expect-error: custom markerIconActive prop
            el.markerIconActive = markerIconActive; // store for later re-use
            // @ts-expect-error: custom markerIconInactive prop
            el.markerIconInactive = markerIconInactive; // store for later re-use
            // @ts-expect-error: custom markerIcon prop
            el.markerIcon = markerIcon; // store for later re-use

            el.addEventListener("mouseenter", () => {
              el.style.backgroundImage = `url(${markerIconActive})`;
              el.style.zIndex = "4";
              el.style.width = mapMarkerActiveWidth;
              el.style.height = mapMarkerActiveHeight;
            });

            el.addEventListener("mouseout", () => {
              el.style.backgroundImage = `url(${markerIcon})`;
              el.style.zIndex = "0";
              el.style.width = mapMarkerWidth;
              el.style.height = mapMarkerHeight;
            });

            // Add eventlistener on map
            // @ts-expect-error: custom "defaultallmarkersstatus" application event, not part of MapLibre's typed MapEventType
            mapSearch.on("defaultallmarkersstatus", () => {
              el.style.backgroundImage = `url(${markerIconInactive})`;
              el.style.zIndex = "0";
              el.style.width = mapMarkerWidth;
              el.style.height = mapMarkerHeight;
              // @ts-expect-error: custom markerIcon prop
              el.markerIcon = markerIconInactive; // store for later re-use
              markerIcon = markerIconInactive;

              // @ts-expect-error: custom activeMarker prop
              el.activeMarker = false;

              // Keep this marker-colored inactive
              el.addEventListener("mouseout", () => {
                el.style.backgroundImage = `url(${markerIconInactive})`;
                el.style.zIndex = "0";
                el.style.width = mapMarkerWidth;
                el.style.height = mapMarkerHeight;
              });
            });

            el.addEventListener("click", (e) => {
              const currentTarget = e.currentTarget as HTMLDivElement;

              /* Important: stop propagation onclick marker. Otherwise onclick map event will also be fired, which again unsets the geohash. */
              e.stopPropagation();

              // @ts-expect-error: custom activeMarker prop
              if (currentTarget.activeMarker === true) {
                // Deactivate
                // @ts-expect-error: custom activeMarker prop
                currentTarget.activeMarker = false;

                currentTarget.style.backgroundImage = `url(${markerIconInactive})`;
                currentTarget.style.zIndex = "0";
                currentTarget.style.width = mapMarkerWidth;
                currentTarget.style.height = mapMarkerHeight;
                // @ts-expect-error: custom markerIcon prop
                currentTarget.markerIcon = markerIconInactive; // store for later re-use
                markerIcon = markerIconInactive;

                // Keep this marker colored inactive
                currentTarget.addEventListener("mouseout", () => {
                  currentTarget.style.backgroundImage = `url(${markerIconInactive})`;
                  currentTarget.style.zIndex = "0";
                  currentTarget.style.width = mapMarkerWidth;
                  currentTarget.style.height = mapMarkerHeight;
                });

                queryBuilderGeohash.delete("geohash");

                const pathname = window.location.pathname;
                const deeplinkUrl = `${pathname}?${queryBuilderGeohash.toString()}`; // Load result page

                searchAction(deeplinkUrl, false);
              } else {
                // Trigger custom event defaultmarkerstatus: to set all other markers to defaultmarkerstatus
                // @ts-expect-error: custom "defaultallmarkersstatus" application event, not part of MapLibre's typed MapEventType
                mapSearch.fire("defaultallmarkersstatus");

                // Activate
                // @ts-expect-error: custom activeMarker prop
                currentTarget.activeMarker = true;

                currentTarget.style.backgroundImage = `url(${markerIconActive})`;
                currentTarget.style.zIndex = "3";
                currentTarget.style.width = mapMarkerActiveWidth;
                currentTarget.style.height = mapMarkerActiveHeight;
                // @ts-expect-error: custom markerIcon prop
                currentTarget.markerIcon = markerIconActive; // store for later re-use
                markerIcon = markerIconActive;

                // Keep this marker colored active
                currentTarget.addEventListener("mouseout", () => {
                  currentTarget.style.backgroundImage = `url(${markerIconActive})`;
                  currentTarget.style.zIndex = "3";
                  currentTarget.style.width = mapMarkerActiveWidth;
                  currentTarget.style.height = mapMarkerActiveHeight;
                });

                queryBuilderGeohash.set("geohash", geohash);

                const pathname = window.location.pathname;
                const deeplinkUrl = `${pathname}?${queryBuilderGeohash.toString()}`; // Load result page

                searchAction(deeplinkUrl, false);
              }
            });

            const marker = new maplibregl.Marker({ element: el })
              .setLngLat([lng, lat])
              .addTo(mapSearch);

            markersList.push(marker);
          },
        );

        mapLoader.classList.add("d-none");
      })
      .catch((error) => {
        Sentry.captureException(error);
      });
  }

  const reload_after_map_move = (e: MapMovementEvent) => {
    // Map initialized for the first time, return
    // @ts-expect-error: custom initialFitBounds prop
    if (e.initialFitBounds) {
      return;
    }

    if (window.getComputedStyle(map).getPropertyValue("display") === "none") {
      return;
    }

    const mapSearch = window.mapSearch;

    const queryBuilder = new URLSearchParams(
      window.location.search.replace(/(%5B)[[\d]+(%5D)/g, "%5B%5D"),
    );

    const ne = mapSearch.getBounds().getNorthEast().wrap(); // Returns a new LngLat object whose longitude is wrapped to the range (-180, 180).
    const sw = mapSearch.getBounds().getSouthWest().wrap(); // Returns a new LngLat object whose longitude is wrapped to the range (-180, 180).

    queryBuilder.set("ne_lat", ne.lat.toString());
    queryBuilder.set("ne_lng", ne.lng.toString());
    queryBuilder.set("sw_lat", sw.lat.toString());
    queryBuilder.set("sw_lng", sw.lng.toString());

    queryBuilder.delete("lat");
    queryBuilder.delete("lng");
    queryBuilder.delete("geohash");
    queryBuilder.delete("page");
    queryBuilder.delete("city_id");
    queryBuilder.delete("distance");
    queryBuilder.delete("location_search");

    const filterDistance = document.querySelector("#searchfilter .distance") as HTMLDivElement;

    if (filterDistance) {
      filterDistance.style.display = "none";
    }

    const pathname = window.location.pathname;
    const deeplinkUrl = `${pathname}?${queryBuilder.toString()}`; // Load result page

    searchAction(deeplinkUrl);
  };

  let mapLoaded = false;

  const initialize_maps = () => {
    /* Bots can affecting the mapbox billing by 60%. So we do not load the map for these bots anymore. */
    const listOfBots = [""];

    if (listOfBots.includes(navigator.userAgent)) {
      const loadingSpinner = search.querySelector(
        ".profile-cards .loading-spinner",
      ) as HTMLDivElement;
      loadingSpinner.style.display = "block";

      return;
    }

    /* Prevent the map being loaded for a second time */
    if (mapLoaded) {
      return;
    }

    if (!supportsWebGl2()) {
      // maplibre-gl throws GPUInitializationError without WebGL2, which would abort the rest of
      // initSearch. Hide the map pane and keep the result list and filters usable.
      console.info("Map disabled: this browser has no WebGL2 support.");
      map.classList.add("d-none");
      buttonMapShow?.classList.add("d-none");
      mapLoaded = true;

      return;
    }

    initialize_search_map(); // Display map and save map variable

    const mapSearch = window.mapSearch;

    mapSearch.on("zoomend", (e) => {
      reload_after_map_move(e);
    });

    mapSearch.on("dragend", (e) => {
      reload_after_map_move(e);
    });

    mapLoaded = true;
  };

  if (window.getComputedStyle(map).getPropertyValue("display") !== "none") {
    initialize_maps(); // Load maps
  }

  const filterAvailability = document.getElementById("filter-availability") as HTMLDivElement;

  if (filterAvailability) {
    const filterAvailabilityInputs: NodeListOf<HTMLInputElement> =
      filterAvailability.querySelectorAll("input");

    filterAvailabilityInputs.forEach((filterAvailabilityInput) => {
      filterAvailabilityInput.addEventListener("change", () => {
        const queryBuilder = new URLSearchParams(
          window.location.search.replace(/(%5B)[[\d]+(%5D)/g, "%5B%5D"),
        );

        queryBuilder.delete(filterAvailabilityInput.getAttribute("name") as string);

        if (filterAvailabilityInput.checked) {
          queryBuilder.set(filterAvailabilityInput.getAttribute("name") as string, "true");
        }

        queryBuilder.delete("page");

        const pathname = window.location.pathname;
        const deeplinkUrl = `${pathname}?${queryBuilder.toString()}`; // Load result page

        searchAction(deeplinkUrl);
      });
    });
  }

  const filtersAge: NodeListOf<HTMLFieldSetElement> = search.querySelectorAll(".filter-age");

  filtersAge.forEach((filterAge) => {
    const minAge = filterAge.querySelector(".min-age") as HTMLInputElement;
    const maxAge = filterAge.querySelector(".max-age") as HTMLInputElement;

    minAge.addEventListener("input", () => {
      const textMinAge = filterAge.querySelector(".text-min") as HTMLSpanElement;
      textMinAge.textContent = minAge.value;

      clearTimeout(minTypingTimer);

      minTypingTimer = setTimeout(doneAgeChange, 400, minAge, maxAge);
    });

    maxAge.addEventListener("input", () => {
      const textMaxAge = filterAge.querySelector(".text-max") as HTMLSpanElement;
      textMaxAge.textContent = maxAge.value === "95" ? "95+" : maxAge.value;

      clearTimeout(maxTypingTimer);

      maxTypingTimer = setTimeout(doneAgeChange, 400, minAge, maxAge);
    });
  });

  function doneAgeChange(minAge: HTMLInputElement, maxAge: HTMLInputElement) {
    const queryBuilder = new URLSearchParams(
      window.location.search.replace(/(%5B)[[\d]+(%5D)/g, "%5B%5D"),
    );

    queryBuilder.delete("min_age");
    queryBuilder.delete("max_age");

    if (minAge.value) {
      queryBuilder.set("min_age", minAge.value);
    }

    if (maxAge.value) {
      queryBuilder.set("max_age", maxAge.value);
    }

    if (queryBuilder.get("min_age") === minAge.min) {
      queryBuilder.delete("min_age");
    }

    if (queryBuilder.get("max_age") === maxAge.max) {
      queryBuilder.delete("max_age");
    }

    queryBuilder.delete("page");

    const pathname = window.location.pathname;
    const deeplinkUrl = `${pathname}?${queryBuilder.toString()}`; // Load result page

    searchAction(deeplinkUrl);
  }

  const filtersExperience: NodeListOf<HTMLDivElement> =
    search.querySelectorAll(".filter-experience");

  filtersExperience.forEach((filterExperience) => {
    const minExperience = filterExperience.querySelector(".min-experience") as HTMLInputElement;
    const maxExperience = filterExperience.querySelector(".max-experience") as HTMLInputElement;

    minExperience.addEventListener("input", () => {
      const textMinExperience = filterExperience.querySelector(".text-min") as HTMLSpanElement;
      textMinExperience.textContent = minExperience.value;

      clearTimeout(minTypingTimer);

      minTypingTimer = setTimeout(doneExperienceChange, 400, minExperience, maxExperience);
    });

    maxExperience.addEventListener("input", () => {
      const textMaxExperience = filterExperience.querySelector(".text-max") as HTMLSpanElement;
      textMaxExperience.textContent = maxExperience.value === "11" ? "11+" : maxExperience.value;

      clearTimeout(maxTypingTimer);

      maxTypingTimer = setTimeout(doneExperienceChange, 400, minExperience, maxExperience);
    });
  });

  function doneExperienceChange(minExperience: HTMLInputElement, maxExperience: HTMLInputElement) {
    const queryBuilder = new URLSearchParams(
      window.location.search.replace(/(%5B)[[\d]+(%5D)/g, "%5B%5D"),
    );

    queryBuilder.delete("min_experience");
    queryBuilder.delete("max_experience");

    if (minExperience.value) {
      queryBuilder.set("min_experience", minExperience.value);
    }

    if (maxExperience.value) {
      queryBuilder.set("max_experience", maxExperience.value);
    }

    if (queryBuilder.get("min_experience") === minExperience.min) {
      queryBuilder.delete("min_experience");
    }

    if (queryBuilder.get("max_experience") === maxExperience.max) {
      queryBuilder.delete("max_experience");
    }

    queryBuilder.delete("page");

    const pathname = window.location.pathname;
    const deeplinkUrl = `${pathname}?${queryBuilder.toString()}`; // Load result page

    searchAction(deeplinkUrl);
  }

  const filtersChildren: NodeListOf<HTMLDivElement> = search.querySelectorAll(".filter-children");

  filtersChildren.forEach((filterChildren) => {
    const minChildren = filterChildren.querySelector(".min-children") as HTMLInputElement;
    const maxChildren = filterChildren.querySelector(".max-children") as HTMLInputElement;

    minChildren.addEventListener("input", () => {
      const textMinChildren = filterChildren.querySelector(".text-min") as HTMLSpanElement;
      textMinChildren.textContent = minChildren.value;

      clearTimeout(minTypingTimer);

      minTypingTimer = setTimeout(doneChildrenChange, 400, minChildren, maxChildren);
    });

    maxChildren.addEventListener("input", () => {
      const textMaxChildren = filterChildren.querySelector(".text-max") as HTMLSpanElement;
      textMaxChildren.textContent = maxChildren.value === "4" ? "4+" : maxChildren.value;

      clearTimeout(maxTypingTimer);

      maxTypingTimer = setTimeout(doneChildrenChange, 400, minChildren, maxChildren);
    });
  });

  function doneChildrenChange(minChildren: HTMLInputElement, maxChildren: HTMLInputElement) {
    const queryBuilder = new URLSearchParams(
      window.location.search.replace(/(%5B)[[\d]+(%5D)/g, "%5B%5D"),
    );

    queryBuilder.delete("min_children");
    queryBuilder.delete("max_children");

    if (minChildren.value) {
      queryBuilder.set("min_children", minChildren.value);
    }

    if (maxChildren.value) {
      queryBuilder.set("max_children", maxChildren.value);
    }

    if (queryBuilder.get("min_children") === minChildren.min) {
      queryBuilder.delete("min_children");
    }

    if (queryBuilder.get("max_children") === maxChildren.max) {
      queryBuilder.delete("max_children");
    }

    queryBuilder.delete("page");

    const pathname = window.location.pathname;
    const deeplinkUrl = `${pathname}?${queryBuilder.toString()}`; // Load result page

    searchAction(deeplinkUrl);
  }

  /*
   * Rate filter
   */
  let minTypingTimer: NodeJS.Timeout;
  let maxTypingTimer: NodeJS.Timeout;

  const minRateAmount = document.getElementById("min-rate-amount") as HTMLInputElement;

  if (minRateAmount) {
    minRateAmount.addEventListener("keyup", () => {
      clearTimeout(minTypingTimer);
      minTypingTimer = setTimeout(doneMinRateTyping, 400);
    });
  }

  const maxRateAmount = document.getElementById("max-rate-amount") as HTMLInputElement;

  if (maxRateAmount) {
    maxRateAmount.addEventListener("keyup", () => {
      clearTimeout(minTypingTimer);
      maxTypingTimer = setTimeout(doneMaxRateTyping, 400);
    });
  }

  //User is "finished typing," do something
  const doneMinRateTyping = () => {
    const queryBuilder = new URLSearchParams(
      window.location.search.replace(/(%5B)[[\d]+(%5D)/g, "%5B%5D"),
    );

    queryBuilder.delete("min_rate_amount");

    if (minRateAmount.value) {
      const currencyAmount = parseFloat(minRateAmount.value);

      // All API requests expect amounts to be provided in a currency’s smallest unit. For 10 USD, provide an amount value of 1000 (that is, 1000 cents). For 10 JPY, provide an amount value of 10 (that is, 10 yen as yen does not have cents).
      const currencyIso = minRateAmount.dataset.currency;
      const currencyFormatted = new Intl.NumberFormat("en-US", {
        currencyDisplay: "code",
        style: "currency",
        currency: currencyIso,
      }).format(currencyAmount); // Return EUR 1.00 and JPY 1

      const currencyAmountSmallestUnit = parseInt(currencyFormatted.replace(/\D/g, ""), 10); // HACK: strips of any non-digits. Returns: 100 (for EUR) and 1 (for JPY)

      queryBuilder.set("min_rate_amount", currencyAmountSmallestUnit.toString());
    }

    queryBuilder.delete("page");

    const pathname = window.location.pathname;
    const deeplinkUrl = `${pathname}?${queryBuilder.toString()}`; // Load result page

    searchAction(deeplinkUrl);
  };

  //User is "finished typing," do something
  const doneMaxRateTyping = () => {
    const queryBuilder = new URLSearchParams(
      window.location.search.replace(/(%5B)[[\d]+(%5D)/g, "%5B%5D"),
    );

    queryBuilder.delete("max_rate_amount");

    if (maxRateAmount.value) {
      const currencyAmount = parseFloat(maxRateAmount.value);

      // All API requests expect amounts to be provided in a currency’s smallest unit. For 10 USD, provide an amount value of 1000 (that is, 1000 cents). For 10 JPY, provide an amount value of 10 (that is, 10 yen as yen does not have cents).
      const currencyIso = maxRateAmount.dataset.currency;
      const currencyFormatted = new Intl.NumberFormat("en-US", {
        currencyDisplay: "code",
        style: "currency",
        currency: currencyIso,
      }).format(currencyAmount); // Return EUR 1.00 and JPY 1

      // HACK: strips of any non-digits. Returns: 100 (for EUR) and 1 (for JPY)
      const currencyAmountSmallestUnit = parseInt(currencyFormatted.replace(/\D/g, ""), 10);

      queryBuilder.set("max_rate_amount", currencyAmountSmallestUnit.toString());
    }

    queryBuilder.delete("page");

    const pathname = window.location.pathname;
    const deeplinkUrl = `${pathname}?${queryBuilder.toString()}`; // Load result page

    searchAction(deeplinkUrl);
  };
};

/**
 Helper functions
 */
function setupRtlPlugin() {
  // setRTLTextPlugin() returns a Promise that rejects if the remote plugin
  // script fails to load (e.g. api.mapbox.com being unreachable for some
  // users/regions) - uncaught, that surfaces as an unhandled rejection
  // (BABYSITS-FRONTEND-11P). Non-fatal either way: the map still works,
  // RTL-script labels just won't render for that session.
  maplibregl
    .setRTLTextPlugin(
      "https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.3.0/mapbox-gl-rtl-text.js",
      true,
    )
    .catch(() => {});
}

function setupFilters(searchDiv: HTMLDivElement) {
  const filterBars: NodeListOf<HTMLDivElement> = searchDiv.querySelectorAll(".filter-bar");

  document.addEventListener("click", (e) => {
    const currentElement = e.target as HTMLElement;
    const currentFilterBar = currentElement.closest(".filter-bar") as HTMLDivElement;

    if (currentFilterBar) {
      return;
    }

    filterBars.forEach((filterBar) => {
      filterBar.classList.remove("active");
    });
  });

  filterBars.forEach((filterBar) => {
    const buttonOpenTooltip = filterBar.querySelector("button.open-tooltip") as HTMLButtonElement;

    buttonOpenTooltip.addEventListener("click", () => {
      /* First, close any open filterbars */
      filterBars.forEach((filterBarToCheck) => {
        /* Do not close the current filterBar */
        if (filterBarToCheck === filterBar) {
          return;
        }

        filterBarToCheck.classList.remove("active");
      });

      /* Than toggle this filterbar */
      filterBar.classList.toggle("active");
    });
  });
}

function setupToggleVideo(searchDiv: HTMLDivElement) {
  const toggleVideos: NodeListOf<HTMLButtonElement> = searchDiv.querySelectorAll(".toggle-video");

  toggleVideos.forEach((toggleVideo) => {
    manageToggleVideo(toggleVideo);
  });
}

function manageToggleVideo(toggleVideo: HTMLButtonElement) {
  toggleVideo.addEventListener("click", () => {
    const videoId = toggleVideo.dataset.videoId as string;
    const video = document.getElementById(videoId) as HTMLVideoElement;

    const iconPlay = toggleVideo.querySelector(".icon-play") as HTMLButtonElement;
    const iconPause = toggleVideo.querySelector(".icon-pause") as HTMLButtonElement;

    if (video.paused) {
      // Ignore AbortError thrown when pause() interrupts this play() request (e.g. rapid toggling)
      video.play().catch(() => undefined);
      video.style.display = "block";

      iconPlay.style.display = "none";
      iconPause.style.display = "block";
    } else {
      video.pause();
      video.style.display = "none";

      iconPlay.style.display = "block";
      iconPause.style.display = "none";
    }

    video.onended = () => {
      iconPlay.style.display = "block";
      iconPause.style.display = "none";
    };
  });
}

function setupMapButtonsEvents(search: HTMLDivElement, body: HTMLBodyElement) {
  const buttonViewResults = search.querySelector("button.view-results") as HTMLButtonElement;

  buttonViewResults.addEventListener("click", () => {
    const searchfilter = document.getElementById("searchfilter") as HTMLDialogElement;

    searchfilter.close();
  });

  const buttonMapHide = search.querySelector("button.map-hide") as HTMLButtonElement;

  if (buttonMapHide) {
    buttonMapHide.addEventListener("click", () => {
      body.classList.remove("mapvisible");
      body.classList.add("maphidden");

      // For smooth transitions between screensizes remove maphidden class after 0,5 seconds
      setTimeout(() => {
        body.classList.remove("maphidden");
      }, 400);
    });
  }
}

function setupShowLanguagesButton(search: HTMLDivElement) {
  const buttonShowAllLanguages = search.querySelector(
    "button.show-all-languages",
  ) as HTMLButtonElement;

  if (buttonShowAllLanguages) {
    buttonShowAllLanguages.addEventListener("click", () => {
      const allLanguages = search.querySelector(".all-languages") as HTMLDivElement;

      allLanguages.style.display = "block";

      buttonShowAllLanguages.style.display = "none";
    });
  }
}

const setMarkerForSearchLocation = (mapSearch: maplibregl.Map, lat: number, lng: number) => {
  /* Set user home address on the map */

  if (!lng || !lat) {
    return;
  }

  // create a DOM element for the marker
  const el = document.createElement("div");

  el.className = "marker-for-search-location";
  el.style.backgroundImage = "url(https://cdn.babysits.com/global/icons/maps/location-dot.svg)";
  el.style.backgroundSize = "cover";
  el.style.width = `${29}px`;
  el.style.height = `${36}px`;
  el.style.zIndex = "0";

  new maplibregl.Marker({ element: el }).setLngLat([lng, lat]).addTo(mapSearch);
};

function manageSavedSearchButtonOneClick(savedSearchButtonOneClick: HTMLButtonElement) {
  savedSearchButtonOneClick.addEventListener("click", () => {
    const searchParams = document.getElementById("saved-search-params") as HTMLInputElement;

    const title = savedSearchButtonOneClick.dataset.title as string;
    const params = JSON.parse(searchParams.value);

    savedSearchButtonOneClick.disabled = true;

    const formData = new FormData();

    formData.append("title", title);
    formData.append("search_params", JSON.stringify(params));

    fetch("/website-api/saved-search/", {
      credentials: "same-origin",
      method: "post",
      body: formData,
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.metadata.code !== 201) {
          return;
        }

        savedSearchButtonOneClick.classList.add("d-none");

        const savedSearchSaved = document.getElementById("saved-search-saved") as HTMLDivElement;
        savedSearchSaved.classList.remove("d-none");
      });
  });
}
